import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, SkipForward, ChevronRight, Wand2, Globe, Zap, Rocket, Crown, Pencil, X, Image } from 'lucide-react';
import { Button } from './ui/button';
import BrandReferenceUpload from './BrandReferenceUpload';
import { ThemeGallery } from './ThemeGallery';
import { supabaseHelpers } from '../lib/supabase';
import { getDefaultModel } from '../utils/models';

// Theme preset options with colors matching aiStylePresets in CreatePPTWorkspace
const THEME_PRESETS = [
  {
    value: 'ai-create',
    label: 'Let AI Create',
    description: 'AI analyzes your content and picks the perfect theme',
    recommended: true,
    colors: null,
  },
  {
    value: 'modern-dark',
    label: 'Modern Dark',
    description: 'Sleek dark theme with vibrant accents',
    colors: { primary: '#a855f7', secondary: '#6366f1', background: '#1a1a2e', text: '#ffffff' },
  },
  {
    value: 'clean-light',
    label: 'Clean Light',
    description: 'Professional light theme',
    colors: { primary: '#3b82f6', secondary: '#1e40af', background: '#ffffff', text: '#1e293b' },
  },
  {
    value: 'bold-gradient',
    label: 'Bold Gradient',
    description: 'Eye-catching gradient backgrounds',
    colors: { primary: '#f97316', secondary: '#ea580c', background: '#7c3aed', text: '#ffffff' },
  },
  {
    value: 'nature-green',
    label: 'Nature Green',
    description: 'Fresh and eco-friendly theme',
    colors: { primary: '#10b981', secondary: '#059669', background: '#ecfdf5', text: '#064e3b' },
  },
  {
    value: 'corporate-blue',
    label: 'Corporate Blue',
    description: 'Traditional business style',
    colors: { primary: '#1e40af', secondary: '#1e3a8a', background: '#f8fafc', text: '#1e293b' },
  },
  {
    value: 'sunset-warm',
    label: 'Sunset Warm',
    description: 'Warm and inviting tones',
    colors: { primary: '#f59e0b', secondary: '#d97706', background: '#1c1917', text: '#fef3c7' },
  },
  {
    value: 'custom',
    label: 'Custom',
    description: 'Describe your own theme or brand',
    colors: null,
    isCustom: true,
  },
];

// Build question list dynamically based on current answers
function getQuestions(answers) {
  const questions = [
    // 1. Audience
    {
      id: 'audience',
      botMessage: "Who is your target audience?",
      options: [
        { label: 'Executives & Leadership', value: 'executives' },
        { label: 'Students & Academics', value: 'students' },
        { label: 'Colleagues & Team', value: 'colleagues' },
        { label: 'Clients & Prospects', value: 'clients' },
        { label: 'General Public', value: 'general' },
      ],
    },
    // 2. Presentation Type
    {
      id: 'presentationType',
      botMessage: "What type of presentation is this?",
      options: [
        { label: 'Pitch / Sales', value: 'pitch' },
        { label: 'Report / Update', value: 'report' },
        { label: 'Educational / Training', value: 'educational' },
        { label: 'Workshop / Interactive', value: 'workshop' },
        { label: 'Informational / Overview', value: 'informational' },
      ],
    },
    // 3. Visual Style (shown for all modes, before mode selection)
    {
      id: 'visualStyle',
      botMessage: "What visual style do you prefer?",
      options: [
        { label: 'Immersive & Visual', value: 'immersive' },
        { label: 'Professional & Neat', value: 'professional' },
        { label: 'Minimal & Clean', value: 'minimal' },
        { label: 'Bold & Creative', value: 'bold' },
      ],
    },
    // 4. Theme & Color Palette (NEW)
    {
      id: 'themePreset',
      botMessage: "Choose a theme & color palette for your presentation:",
      type: 'theme-preset-cards',
      options: THEME_PRESETS,
    },
    // 5. Generation Mode
    {
      id: 'generationMode',
      botMessage: "Which generation mode would you like to use?",
      type: 'mode-cards',
      options: [
        {
          label: 'Imaginative',
          value: 'ai',
          description: 'Super creative, takes a few minutes, can only be edited via a prompt',
          icon: 'Wand2',
          gradient: 'from-purple-500 to-pink-500',
        },
        {
          label: 'Intelligent',
          value: 'html',
          description: 'Professional slides, rich visuals & infographics, takes a few minutes, fully editable',
          icon: 'Globe',
          gradient: 'from-blue-500 to-cyan-500',
        },
      ],
    },
  ];

  // 6. Speed/quality — only for Intelligent mode
  if (answers.generationMode === 'html') {
    questions.push({
      id: 'htmlModel',
      botMessage: "Choose your speed vs quality preference:",
      type: 'speed-cards',
      options: [
        {
          label: 'Fast',
          value: 'flash',
          description: '~2x faster, great quality, optimized for code',
          subtext: 'Gemini 3 Flash',
          icon: 'Rocket',
          gradient: 'from-cyan-500 to-blue-500',
        },
        {
          label: 'Balanced',
          value: 'sonnet',
          description: 'Fast and high quality, a great default',
          subtext: 'Claude Sonnet 5',
          icon: 'Wand2',
          gradient: 'from-violet-500 to-purple-500',
        },
        {
          label: 'Premium',
          value: 'opus',
          description: 'Best visual quality, richer designs, slower',
          subtext: 'Claude Opus 5',
          icon: 'Crown',
          gradient: 'from-amber-500 to-orange-500',
        },
        {
          label: 'GPT-5.6 Sol',
          value: 'sol',
          description: 'OpenAI flagship — premium tier, slightly pricier than Opus',
          subtext: 'GPT-5.6 Sol',
          icon: 'Crown',
          gradient: 'from-emerald-500 to-teal-500',
        },
        {
          label: 'GPT-6 Astra',
          value: 'astra',
          description: 'OpenAI frontier model — Ultra tier, 2× Premium credits',
          subtext: 'GPT-6 Astra',
          icon: 'Crown',
          gradient: 'from-violet-500 to-fuchsia-500',
        },
        {
          label: 'Kimi K3',
          value: 'kimi',
          description: 'Moonshot flagship — 1M context, Sonnet-class pricing',
          subtext: 'Moonshot Kimi K3',
          icon: 'Crown',
          gradient: 'from-violet-500 to-purple-500',
        },
      ],
    });
  }

  // 7. Theme gallery — only for Instant mode (template-specific themes)
  if (answers.generationMode === 'templates') {
    questions.push({
      id: 'themeSelection',
      botMessage: "Choose a template theme for your presentation.",
      type: 'theme-gallery',
    });
  }

  // Brand upload — only for Imaginative + brand-reference (kept for future use)
  if (answers.generationMode === 'ai' && answers.visualStyle === 'brand-reference') {
    questions.push({
      id: 'brandUpload',
      botMessage: "Upload your brand reference slides. These will guide the visual style of your presentation.",
      type: 'brand-upload',
    });
  }

  // 8. Include images — always shown
  questions.push({
    id: 'includeImages',
    botMessage: "Would you like to include images in your slides?",
    options: [
      { label: 'Yes, include images (adds ~30s per slide)', value: 'yes' },
      { label: 'No, text and graphics only', value: 'no' },
    ],
  });

  // 9. Image style — only when images are included
  if (answers.includeImages === 'yes') {
    questions.push({
      id: 'imageStyle',
      botMessage: "How would you like images integrated into your slides?",
      options: [
        { label: 'Cinematic backgrounds (all slides)', value: 'background' },
        { label: 'Inline photos (embedded within slide layouts)', value: 'inline' },
      ],
    });
  }

  // 10. Logo upload — always shown last
  questions.push({
    id: 'logoUpload',
    botMessage: "Want to add your logo to every slide? Upload it here, or skip.",
    type: 'logo-upload',
  });

  return questions;
}

const MODE_ICONS = { Wand2, Globe, Zap, Rocket, Crown };

export default function PresentationQuestionnaire({ onComplete, onSkipAll, inputMode, userId, brandGuidelines, initialAnswers }) {
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({
    audience: null, presentationType: null, visualStyle: null, includeImages: null, imageStyle: null,
    generationMode: null, htmlModel: null, selectedTheme: null, brandReferences: null,
    themePreset: null, customThemeRequest: '',
    ...(initialAnswers || {}),
  });
  const [showingOptions, setShowingOptions] = useState(false);
  const [brandReferences, setBrandReferences] = useState({ images: [], weight: 'medium' });
  const [localSelectedTheme, setLocalSelectedTheme] = useState(null);
  const [customThemeText, setCustomThemeText] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const scrollRef = useRef(null);
  const logoInputRef = useRef(null);

  const questions = getQuestions(answers);
  const currentQ = questions[currentQuestionIndex];

  // Show first question on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const qs = getQuestions(answers);
      setMessages([{ role: 'bot', content: qs[0].botMessage }]);
      setTimeout(() => setShowingOptions(true), 200);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, showingOptions]);

  const advanceToNext = (updatedAnswers, nextIndex) => {
    const nextQuestions = getQuestions(updatedAnswers);
    if (nextIndex >= nextQuestions.length) {
      // All questions answered — build final result
      const result = {
        audience: updatedAnswers.audience,
        presentationType: updatedAnswers.presentationType,
        visualStyle: updatedAnswers.visualStyle,
        includeImages: updatedAnswers.includeImages,
        imageStyle: updatedAnswers.imageStyle || 'background',
        generationMode: updatedAnswers.generationMode || 'html',
        htmlModel: updatedAnswers.htmlModel || getDefaultModel(),
        selectedTheme: updatedAnswers.selectedTheme || null,
        brandReferences: updatedAnswers.brandReferences || null,
        aiStylePreset: updatedAnswers.themePreset || 'ai-create',
        customThemeRequest: updatedAnswers.customThemeRequest || null,
        logoUrl: updatedAnswers.logoUrl || null,
      };
      setTimeout(() => {
        setMessages(prev => [...prev, { role: 'bot', content: "Great, let's create your presentation!" }]);
        setTimeout(() => onComplete(result), 600);
      }, 400);
    } else {
      setCurrentQuestionIndex(nextIndex);
      const nextQ = nextQuestions[nextIndex];
      setTimeout(() => {
        setMessages(prev => [...prev, { role: 'bot', content: nextQ.botMessage }]);
        setTimeout(() => setShowingOptions(true), 200);
      }, 400);
    }
  };

  const handleAnswer = (questionId, value, label) => {
    const updatedAnswers = { ...answers, [questionId]: value };

    // If mode changed, reset mode-dependent answers (but NOT visualStyle or themePreset — they come before mode)
    if (questionId === 'generationMode') {
      updatedAnswers.htmlModel = null;
      updatedAnswers.selectedTheme = null;
      updatedAnswers.brandReferences = null;
    }

    setAnswers(updatedAnswers);
    setShowingOptions(false);
    setMessages(prev => [...prev, { role: 'user', content: label }]);

    advanceToNext(updatedAnswers, currentQuestionIndex + 1);
  };

  const handleComponentContinue = (questionId, value, label) => {
    const updatedAnswers = { ...answers, [questionId]: value };
    setAnswers(updatedAnswers);
    setShowingOptions(false);
    setMessages(prev => [...prev, { role: 'user', content: label }]);
    advanceToNext(updatedAnswers, currentQuestionIndex + 1);
  };

  const handleSkip = () => {
    setShowingOptions(false);
    setMessages(prev => [...prev, { role: 'user', content: 'Skipped' }]);

    // For skipped mode question, default to Intelligent
    const updatedAnswers = { ...answers };
    if (currentQ?.id === 'generationMode') {
      updatedAnswers.generationMode = 'html';
      setAnswers(updatedAnswers);
    }

    advanceToNext(updatedAnswers, currentQuestionIndex + 1);
  };

  const handleSkipAll = () => {
    onSkipAll();
  };

  const handleLogoSelect = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));

    // Upload to Supabase
    if (userId) {
      setLogoUploading(true);
      try {
        const { data, error } = await supabaseHelpers.uploadBrandAsset(userId, file, 'logo');
        if (error) throw error;
        const updatedAnswers = { ...answers, logoUrl: data.url };
        setAnswers(updatedAnswers);
        setLogoUploading(false);
      } catch (err) {
        console.error('Logo upload failed:', err);
        setLogoUploading(false);
      }
    }
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleLogoSelect(file);
  };

  return (
    <div className="flex flex-col" style={{ minHeight: '400px', maxHeight: '75vh' }}>
      {/* Chat messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                msg.role === 'user'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-foreground border border-border/40'
              }`}
            >
              {msg.role === 'bot' && (
                <Sparkles className="w-3.5 h-3.5 text-purple-500 inline mr-1.5 -mt-0.5" />
              )}
              {msg.content}
            </div>
          </div>
        ))}

        {/* Answer options for current question */}
        {showingOptions && currentQ && (
          <div className="animate-fade-in-up pl-2">
            {/* Mode selection cards */}
            {currentQ.type === 'mode-cards' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                {currentQ.options.map((opt) => {
                  const Icon = MODE_ICONS[opt.icon];
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(currentQ.id, opt.value, opt.label)}
                      className="group relative p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700
                        bg-white dark:bg-slate-900 hover:border-purple-400 dark:hover:border-purple-500
                        transition-all text-left hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${opt.gradient} flex items-center justify-center mb-3`}>
                        {Icon && <Icon className="w-5 h-5 text-white" />}
                      </div>
                      <div className="font-semibold text-sm text-foreground mb-1">{opt.label}</div>
                      <div className="text-xs text-muted-foreground leading-relaxed">{opt.description}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Speed/quality cards */}
            {currentQ.type === 'speed-cards' && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                {currentQ.options.map((opt) => {
                  const Icon = MODE_ICONS[opt.icon];
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(currentQ.id, opt.value, opt.label)}
                      className="group relative p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700
                        bg-white dark:bg-slate-900 hover:border-purple-400 dark:hover:border-purple-500
                        transition-all text-left hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${opt.gradient} flex items-center justify-center mb-3`}>
                        {Icon && <Icon className="w-5 h-5 text-white" />}
                      </div>
                      <div className="font-semibold text-sm text-foreground mb-1">{opt.label}</div>
                      <div className="text-xs text-muted-foreground leading-relaxed">{opt.description}</div>
                      {opt.subtext && (
                        <div className="text-[10px] text-muted-foreground/60 mt-1.5 font-mono">{opt.subtext}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Theme & Color Palette cards (NEW) */}
            {currentQ.type === 'theme-preset-cards' && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {currentQ.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setAnswers(prev => ({ ...prev, themePreset: opt.value }));
                        if (!opt.isCustom) setCustomThemeText('');
                      }}
                      className={`relative p-3 rounded-xl border-2 transition-all text-left
                        ${answers.themePreset === opt.value
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-purple-300 dark:hover:border-purple-600'}
                      `}
                    >
                      {opt.recommended && (
                        <span className="absolute -top-2 right-2 px-1.5 py-0.5 text-[9px] font-bold
                          bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full leading-tight">
                          Recommended
                        </span>
                      )}
                      {/* Color swatches for preset themes */}
                      {opt.colors && (
                        <div className="flex gap-1.5 mb-2">
                          {Object.values(opt.colors).map((color, ci) => (
                            <div
                              key={ci}
                              className="w-5 h-5 rounded-full border border-slate-300 dark:border-slate-600"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      )}
                      {/* AI sparkle icon for ai-create */}
                      {opt.value === 'ai-create' && (
                        <Sparkles className="w-5 h-5 text-purple-500 mb-1.5" />
                      )}
                      {/* Pencil icon for custom */}
                      {opt.isCustom && (
                        <Pencil className="w-5 h-5 text-slate-400 mb-1.5" />
                      )}
                      <div className="font-semibold text-xs text-foreground">{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{opt.description}</div>
                    </button>
                  ))}
                </div>

                {/* Custom text input — shown when "Custom" is selected */}
                {answers.themePreset === 'custom' && (
                  <input
                    type="text"
                    placeholder='e.g., "Accenture brand colors" or "dark red and gold luxury theme"'
                    value={customThemeText}
                    onChange={(e) => setCustomThemeText(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-purple-300 dark:border-purple-700
                      bg-white dark:bg-slate-900 text-foreground placeholder:text-muted-foreground/50
                      focus:outline-none focus:ring-2 focus:ring-purple-400"
                    autoFocus
                  />
                )}

                {/* Continue button */}
                <Button
                  size="sm"
                  disabled={!answers.themePreset || (answers.themePreset === 'custom' && !customThemeText.trim())}
                  onClick={() => {
                    const updatedAnswers = {
                      ...answers,
                      themePreset: answers.themePreset,
                      customThemeRequest: answers.themePreset === 'custom' ? customThemeText.trim() : '',
                    };
                    setAnswers(updatedAnswers);
                    setShowingOptions(false);
                    const selectedOpt = currentQ.options.find(o => o.value === answers.themePreset);
                    const label = answers.themePreset === 'custom'
                      ? `Custom: "${customThemeText.trim()}"`
                      : selectedOpt?.label || answers.themePreset;
                    setMessages(prev => [...prev, { role: 'user', content: label }]);
                    advanceToNext(updatedAnswers, currentQuestionIndex + 1);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Brand upload component */}
            {currentQ.type === 'brand-upload' && (
              <div className="mt-3 space-y-3">
                <BrandReferenceUpload
                  onReferencesChange={(refs) => setBrandReferences(refs)}
                  initialReferences={brandReferences.images}
                  initialWeight={brandReferences.weight}
                  userId={userId}
                  compact={true}
                />
                <Button
                  size="sm"
                  onClick={() => handleComponentContinue('brandReferences', brandReferences, `Uploaded ${brandReferences.images.length} reference(s)`)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Theme gallery component (for Instant mode templates) */}
            {currentQ.type === 'theme-gallery' && (
              <div className="mt-3 space-y-3">
                <ThemeGallery
                  selectedTheme={localSelectedTheme}
                  onSelectTheme={setLocalSelectedTheme}
                  brandGuidelines={brandGuidelines}
                />
                <Button
                  size="sm"
                  disabled={!localSelectedTheme}
                  onClick={() => handleComponentContinue('selectedTheme', localSelectedTheme, `Theme: ${localSelectedTheme?.name || 'Selected'}`)}
                  className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {/* Logo upload */}
            {currentQ.type === 'logo-upload' && (
              <div className="mt-3 space-y-3">
                {!logoPreview ? (
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleLogoDrop}
                    onClick={() => logoInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-6
                      hover:border-purple-400 dark:hover:border-purple-500 transition-colors cursor-pointer
                      flex flex-col items-center gap-2 text-center"
                  >
                    <Image className="w-8 h-8 text-slate-400" />
                    <p className="text-sm text-muted-foreground">
                      {logoUploading ? 'Uploading...' : 'Drop your logo here or click to browse'}
                    </p>
                    <p className="text-xs text-muted-foreground/60">PNG, SVG, or JPG</p>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/svg+xml,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={(e) => handleLogoSelect(e.target.files?.[0])}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-border/40">
                    <img src={logoPreview} alt="Logo" className="h-12 w-auto object-contain rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{logoFile?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {logoUploading ? 'Uploading...' : 'Ready'}
                      </p>
                    </div>
                    <button
                      onClick={() => { setLogoFile(null); setLogoPreview(null); setAnswers(prev => ({ ...prev, logoUrl: null })); }}
                      className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={logoUploading}
                    onClick={() => handleComponentContinue('logoUrl', answers.logoUrl || null, answers.logoUrl ? `Logo uploaded` : 'No logo')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {answers.logoUrl ? 'Continue with logo' : 'Skip'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Standard pill-button options */}
            {!currentQ.type && (
              <div className="flex flex-wrap gap-2 mt-2">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(currentQ.id, opt.value, opt.label)}
                    className="px-3.5 py-2 text-sm rounded-full border-2 border-purple-200 dark:border-purple-800
                      bg-white dark:bg-slate-900 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30
                      transition-all font-medium text-foreground"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="flex items-center justify-between border-t border-border/40 pt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkipAll}
          className="text-muted-foreground hover:text-foreground"
        >
          <SkipForward className="w-4 h-4 mr-1.5" />
          Skip All
        </Button>
        {showingOptions && currentQ && !currentQ.type && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground"
          >
            Skip
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
